// biome-ignore lint/style/useImportType: Classic JSX runtime requires React.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { InstanceScope } from "./processScopeModel";

export default function ProcessScopeRangeEditor({
  kind,
  scope,
  onChange,
  onPicked,
}: {
  kind: "duration" | "steps";
  scope: InstanceScope;
  onChange: (scope: InstanceScope) => void;
  onPicked: () => void;
}) {
  const { t } = useTranslation();
  const duration = kind === "duration";
  const [unit, setUnit] = useState(60);
  const [minimum, setMinimum] = useState(() => {
    const value = duration ? scope.minDuration : scope.minSteps;
    return value === undefined ? "" : String(value / (duration ? 60 : 1));
  });
  const [maximum, setMaximum] = useState(() => {
    const value = duration ? scope.maxDuration : scope.maxSteps;
    return value === undefined ? "" : String(value / (duration ? 60 : 1));
  });
  const min = minimum === "" ? undefined : Number(minimum);
  const max = maximum === "" ? undefined : Number(maximum);
  const invalid =
    [min, max].some(
      value =>
        value !== undefined &&
        (!Number.isFinite(value) || value < 0 || (!duration && !Number.isInteger(value))),
    ) ||
    (min !== undefined && max !== undefined && min > max);
  return (
    <form
      className="space-y-3 p-3"
      onSubmit={event => {
        event.preventDefault();
        if (invalid) return;
        onChange(
          duration
            ? {
                ...scope,
                minDuration: min === undefined ? undefined : min * unit,
                maxDuration: max === undefined ? undefined : max * unit,
              }
            : { ...scope, minSteps: min, maxSteps: max },
        );
        onPicked();
      }}
    >
      <p className="text-xs text-text-mid">
        {t(duration ? "processPage.durationFilterHelp" : "processPage.stepCountHelp")}
      </p>
      {duration && (
        <label className="flex items-center justify-between gap-2 text-sm text-text">
          {t("processPage.durationUnit")}
          <select
            value={unit}
            onChange={event => {
              const next = Number(event.target.value);
              if (min !== undefined) setMinimum(String((min * unit) / next));
              if (max !== undefined) setMaximum(String((max * unit) / next));
              setUnit(next);
            }}
            className="rounded-md border border-outline bg-surface px-2 py-1.5"
          >
            <option value={60}>{t("processPage.minutesUnit")}</option>
            <option value={3600}>{t("processPage.hoursUnit")}</option>
          </select>
        </label>
      )}
      <div className="grid grid-cols-2 gap-3">
        {(
          [
            ["minimum", minimum, setMinimum],
            ["maximum", maximum, setMaximum],
          ] as const
        ).map(([label, value, setValue]) => (
          <label key={label} className="space-y-1 text-xs text-text-mid">
            <span>{t(`processPage.${label}`)}</span>
            <input
              type="number"
              min={0}
              step={duration ? "any" : 1}
              value={value}
              onChange={event => setValue(event.target.value)}
              placeholder={t("processPage.noLimit")}
              aria-invalid={invalid}
              className="w-full rounded-md border border-outline bg-surface px-3 py-2 text-sm text-text outline-none focus:border-info"
            />
          </label>
        ))}
      </div>
      {invalid && (
        <p role="alert" className="text-xs text-text-mid">
          {t("processPage.invalidRange")}
        </p>
      )}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={invalid}
          className="rounded-md px-3 py-1.5 text-sm font-medium text-info hover:bg-muted-surface disabled:opacity-40"
        >
          {t("scope_filter_done")}
        </button>
      </div>
    </form>
  );
}
